import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Trash } from 'react-feather';
import { useDispatch } from 'react-redux';
import { Button, ButtonGroup, Card, CardBody, Col, Input, Row } from 'reactstrap';
import debounce from 'lodash.debounce';
import AddExhibitorModal from './AddExhibitorModal';
import UploadExhibitorsModal from './UploadExhibitorsModal';
import ConfirmationModal from '../ConfirmationModal';
import {
  deleteSeasonExhibitors,
  getSeasonExhibitors,
} from '../../ReduxToolkit/Reducers/fairManagement';

// One season's exhibitors (the fair's Invited Profiles data): by type and New / Rebooked tab, with
// add, CSV upload and delete. onChanged refreshes the season counts.
const SeasonExhibitors = ({ organizationId, season, onChanged }) => {
  const dispatch = useDispatch();
  const [type, setType] = useState('brand');
  const [reBooked, setReBooked] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [query, setQuery] = useState('');
  const [data, setData] = useState({ items: [], total: 0, tabs: { new: 0, rebooked: 0 } });
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [clearSelection, setClearSelection] = useState(false);
  const [deleteIds, setDeleteIds] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = await dispatch(
      getSeasonExhibitors(organizationId, {
        fair_id: season.id,
        type,
        re_booked: reBooked,
        skip: page,
        take: perPage,
        query,
      })
    );
    setIsLoading(false);
    if (result) setData(result);
    setSelected([]);
    setClearSelection((value) => !value);
  }, [dispatch, organizationId, season.id, type, reBooked, page, perPage, query]);

  useEffect(() => {
    load();
  }, [load]);

  // A new type, tab or search starts on page 1 (the page remounts this per season)
  const showType = (value) => {
    setPage(1);
    setType(value);
  };
  const showTab = (value) => {
    setPage(1);
    setReBooked(value);
  };
  const search = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setQuery(value.trim());
      }, 500),
    []
  );

  const afterChange = () => {
    load();
    onChanged?.();
  };

  const handleDelete = async () => {
    setDeleting(true);
    const result = await dispatch(deleteSeasonExhibitors(organizationId, deleteIds));
    setDeleting(false);
    if (result) {
      setDeleteIds(null);
      afterChange();
    }
  };

  const columns = [
    { name: 'Name', selector: (row) => row.name, wrap: true, grow: 2 },
    { name: 'Website', selector: (row) => row.website, wrap: true, grow: 2 },
    { name: 'Email', selector: (row) => row.email || '--', wrap: true, grow: 2 },
    { name: 'Registered', selector: (row) => (row.paid ? 'Yes' : 'No'), width: '110px' },
    {
      name: 'Invitation',
      selector: (row) => (row.invite_status ? row.invite_status : 'Not invited'),
      width: '120px',
    },
    {
      name: 'Added',
      selector: (row) => new Date(row.created_at).toLocaleDateString(),
      width: '110px',
    },
    {
      name: '',
      width: '60px',
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-danger"
          title="Remove from this season"
          onClick={() => setDeleteIds([row.id])}
        >
          <Trash size={16} />
        </button>
      ),
    },
  ];

  return (
    <Card className="shadow-sm mb-4">
      <CardBody>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <h5 className="mb-0">
            Exhibitors of {season.season_name} {season.year}
          </h5>
          <div className="d-flex flex-wrap gap-2">
            {!!selected.length && (
              <Button color="danger" outline onClick={() => setDeleteIds(selected.map((r) => r.id))}>
                Delete selected ({selected.length})
              </Button>
            )}
            <Button color="primary" outline onClick={() => setAddOpen(true)}>
              Add exhibitor
            </Button>
            <Button color="primary" onClick={() => setUploadOpen(true)}>
              Upload CSV
            </Button>
          </div>
        </div>

        <Row className="g-2 mb-3 align-items-center">
          <Col xs="auto">
            <ButtonGroup>
              <Button color="primary" outline={type !== 'brand'} onClick={() => showType('brand')}>
                Brands
              </Button>
              <Button
                color="primary"
                outline={type !== 'retailer'}
                onClick={() => showType('retailer')}
              >
                Retailers
              </Button>
            </ButtonGroup>
          </Col>
          <Col xs="auto">
            <ButtonGroup>
              <Button color="secondary" outline={reBooked} onClick={() => showTab(false)}>
                New ({data.tabs.new})
              </Button>
              <Button color="secondary" outline={!reBooked} onClick={() => showTab(true)}>
                Rebooked ({data.tabs.rebooked})
              </Button>
            </ButtonGroup>
          </Col>
          <Col md={4}>
            <Input
              placeholder="Search name, website or email"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                search(e.target.value);
              }}
            />
          </Col>
        </Row>

        <DataTable
          striped
          pagination
          paginationServer
          selectableRows
          data={data.items}
          columns={columns}
          progressPending={isLoading}
          paginationTotalRows={data.total}
          paginationPerPage={perPage}
          paginationDefaultPage={page}
          clearSelectedRows={clearSelection}
          onSelectedRowsChange={({ selectedRows }) => setSelected(selectedRows)}
          onChangePage={setPage}
          onChangeRowsPerPage={(count) => {
            setPage(1);
            setPerPage(count);
          }}
          noDataComponent={<div className="p-4 text-muted">No exhibitors here yet</div>}
        />
      </CardBody>

      <ConfirmationModal
        open={!!deleteIds}
        onClose={() => setDeleteIds(null)}
        isLoading={deleting}
        onSubmit={handleDelete}
        title="Remove exhibitors"
        message={`Remove ${deleteIds?.length} exhibitor(s) from ${season.season_name} ${season.year}? Their credit usage in this season is deleted too. Accounts they created stay.`}
        confirmButtonText="Remove"
      />
      <AddExhibitorModal
        open={addOpen}
        organizationId={organizationId}
        season={season}
        defaultType={type}
        onClose={() => setAddOpen(false)}
        onAdded={afterChange}
      />
      <UploadExhibitorsModal
        open={uploadOpen}
        organizationId={organizationId}
        season={season}
        onClose={() => setUploadOpen(false)}
        onUploaded={afterChange}
      />
    </Card>
  );
};

export default SeasonExhibitors;
